"use client";

import React, { useState } from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Paper,
  Avatar,
  Typography,
  CardContent,
  TextField,
  Button,
  InputAdornment,
  Box,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Email,
  Send,
  Dashboard,
  LockOutlined,
  Password,
  Login as LoginIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../../Action/userActions";
import "./Login.css";
import { RECAPTCHA_ACTION_REQUEST_OTP , executeRecaptcha } from "../../services/recaptcha";

const theme = createTheme({
  palette: {
    primary: { main: "#1976d2" },
    secondary: { main: "#9c27b0" },
    background: { default: "#f5f5f5" },
  },
  typography: {
    fontFamily: "'Roboto', 'Arial', sans-serif",
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

export default function LoginPage() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const muiTheme = useTheme();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    const allowedDomainRegex = /^[a-zA-Z0-9._%+-]+@bizprospex\.com$/;
    if (!allowedDomainRegex.test(email)) {
      setError("Only @bizprospex.com emails are allowed.");
      setLoading(false);
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION_REQUEST_OTP);
      const res = await fetch("/api/v1/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, recaptchaToken }),
      });   
      const data = await res.json();

      if (res.ok) {
        setStep(2);
        setMessage("OTP sent to your email.");
      } else {
        setError(data.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error("Network error in handleSendOtp:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setVerifying(true);

    dispatch(loginUser(email, otp))
      .then(() => {
        setMessage("Login successful!");
        setTimeout(() => {
          navigate("/dashboard");
        }, 1000);
      })
      .catch((error) => {
        setError(error.message || "Invalid OTP");
      })
      .finally(() => {
        setVerifying(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="login-wrapper">
        <Container maxWidth="sm">
          <Paper elevation={24} className="login-paper">
            <Box className="login-header">
              <Avatar className="login-avatar">
                <Dashboard sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h5">ExhibitorsData</Typography>
              <Typography variant="subtitle1">Dashboard</Typography>
            </Box>

            <CardContent sx={{ padding: 3 }}>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    margin: "0 auto 12px",
                    background: muiTheme.palette.primary.main,
                  }}
                >
                  <LockOutlined />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Welcome Back
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step === 1
                    ? "Enter your @bizprospex.com email to receive an OTP"
                    : "Enter OTP to log in"}
                </Typography>
              </Box>

              <form onSubmit={step === 1 ? handleSendOtp : handleVerifyOtp}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                  required
                  disabled={step === 2}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />

                {step === 2 && (
                  <TextField
                    fullWidth
                    label="OTP"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    variant="outlined"
                    required
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Password color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  size="large"
                  startIcon={
                    step === 1 ? <Send fontSize="small" /> : <LoginIcon fontSize="small" />
                  }
                  disabled={step === 1 ? loading || !email : verifying || !otp}
                  sx={{
                    py: 1.25,
                    fontWeight: 600,
                    fontSize: "1rem",
                    textTransform: "none",
                    background:
                      step === 1
                        ? `linear-gradient(45deg, ${muiTheme.palette.primary.main}, ${muiTheme.palette.primary.dark})`
                        : muiTheme.palette.secondary.main,
                    boxShadow: `0 4px 20px ${alpha(
                      step === 1
                        ? muiTheme.palette.primary.main
                        : muiTheme.palette.secondary.main,
                      0.4
                    )}`,
                    "&:hover": {
                      background:
                        step === 1
                          ? `linear-gradient(45deg, ${muiTheme.palette.primary.dark}, ${muiTheme.palette.primary.main})`
                          : muiTheme.palette.secondary.dark,
                    },
                  }}
                >
                  {step === 1
                    ? loading
                      ? "Sending..."
                      : "Send OTP"
                    : verifying
                    ? "Verifying..."
                    : "Login"}
                </Button>

                {error && (
                  <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                    {error}
                  </Typography>
                )}
                {message && (
                  <Typography color="primary" variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                    {message}
                  </Typography>
                )}
              </form>

              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {"Don't have an account? "}
                  <Button variant="text" sx={{ textTransform: "none", fontWeight: 600, padding: 0 }}>
                    Contact Admin
                  </Button>
                </Typography>
              </Box>
            </CardContent>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
